import { useRef, useState } from 'react';
import { colorOf, WHEEL_ORDER, type RouletteBet, type RouletteState } from '../../engine/games/roulette';
import { money } from '../../format';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

const SPIN_MS = 2600;
const LOST_MS = 900;
const BANNER_MS = 1100;
const END_HOLD_MS = 300;
const N = WHEEL_ORDER.length;

const OUTSIDE: { bet: RouletteBet; label: string; pays: string }[] = [
  { bet: 'red', label: 'Red', pays: '1:1' },
  { bet: 'black', label: 'Black', pays: '1:1' },
  { bet: 'odd', label: 'Odd', pays: '1:1' },
  { bet: 'even', label: 'Even', pays: '1:1' },
  { bet: 'low', label: '1–18', pays: '1:1' },
  { bet: 'high', label: '19–36', pays: '1:1' },
  { bet: 'dozen1', label: '1st 12', pays: '2:1' },
  { bet: 'dozen2', label: '2nd 12', pays: '2:1' },
  { bet: 'dozen3', label: '3rd 12', pays: '2:1' },
];

const betName = (s: RouletteState) => (s.bet === 'straight' ? `Number ${s.number}` : OUTSIDE.find((o) => o.bet === s.bet)!.label);

/** Where on the wheel (0..1 of a turn) a pocket sits. */
const angleOf = (n: number) => WHEEL_ORDER.indexOf(n) / N;

function Wheel({ ballAt, landed }: { ballAt: number; landed: number | null }) {
  const R = 100;
  return (
    <svg viewBox="-110 -110 220 220" className="wheel" role="img" aria-label={landed === null ? 'Roulette wheel' : `Ball on ${landed} ${colorOf(landed)}`}>
      <circle r={R + 8} className="wheel-rim" />
      {WHEEL_ORDER.map((n, i) => {
        const a0 = (i / N) * Math.PI * 2 - Math.PI / 2 - Math.PI / N;
        const a1 = a0 + (Math.PI * 2) / N;
        const p = (a: number, r: number) => `${(Math.cos(a) * r).toFixed(2)},${(Math.sin(a) * r).toFixed(2)}`;
        const mid = (a0 + a1) / 2;
        return (
          <g key={n}>
            <path d={`M ${p(a0, 58)} L ${p(a0, R)} A ${R} ${R} 0 0 1 ${p(a1, R)} L ${p(a1, 58)} A 58 58 0 0 0 ${p(a0, 58)} Z`} className={`pocket ${colorOf(n)}${landed === n ? ' hit' : ''}`} />
            <text x={Math.cos(mid) * 86} y={Math.sin(mid) * 86 + 3} textAnchor="middle" className="pocket-num" transform={`rotate(${(mid * 180) / Math.PI + 90} ${Math.cos(mid) * 86} ${Math.sin(mid) * 86})`}>{n}</text>
          </g>
        );
      })}
      <circle r="56" className="wheel-hub" />
      <text y="10" textAnchor="middle" className={`hub-num ${landed === null ? '' : colorOf(landed)}`}>{landed ?? ''}</text>
      <circle cx={Math.cos(ballAt * Math.PI * 2 - Math.PI / 2) * 70} cy={Math.sin(ballAt * Math.PI * 2 - Math.PI / 2) * 70} r="6" className="ball" />
    </svg>
  );
}

export function RouletteTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.roulette?.state as RouletteState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  const lost = luck ? Number(luck.match(/Ball: (\d+)/)?.[1] ?? NaN) : NaN;
  const hasLost = Number.isFinite(lost);
  const firstEnd = hasLost ? SPIN_MS + LOST_MS + BANNER_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, firstEnd + SPIN_MS + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();
  const [numbers, setNumbers] = useState(false);
  useLuckMoment(snapshot, elapsed, hasLost ? SPIN_MS + LOST_MS : null, () => ({ what: 'A losing spin is spun again.', saved: !!state?.lucky }), setBurst);

  const ball = state?.history[0];
  const prev = state?.history[1] ?? 0;
  // The ball runs round a few times and slows into its pocket.
  const spinTo = (from: number, to: number, t: number) => {
    const k = Math.min(1, t / SPIN_MS);
    const ease = 1 - (1 - k) ** 3;
    const travel = 3 + ((angleOf(to) - angleOf(from) + 1) % 1);
    return (angleOf(from) + travel * ease) % 1;
  };
  let ballAt = ball !== undefined ? angleOf(ball) : 0;
  let landed: number | null = anim ? null : ball ?? null;
  if (anim && ball !== undefined) {
    if (hasLost && elapsed < firstEnd) {
      ballAt = spinTo(prev, lost, elapsed);
      if (elapsed >= SPIN_MS) landed = lost;
    } else {
      const t = elapsed - firstEnd;
      ballAt = spinTo(hasLost ? lost : prev, ball, t);
      if (t >= SPIN_MS) landed = ball;
    }
  }

  // Changing the bet walks the engine's menus: Bet on -> type -> number.
  const choose = (bet: RouletteBet, n?: number) => {
    if (!state || anim) return;
    actions.tableOption('pick');
    actions.tableOption(`type:${bet}`);
    if (bet === 'straight' && n !== undefined) actions.tableOption(`num:${n}`);
    setNumbers(false);
  };

  // The bet the last spin was on, remembered while it spins (the bet can't
  // change mid-spin), so choosing the next bet doesn't relabel the last
  // result (found in a Codex review: a win on Red read "Black +$...").
  const spunBet = useRef<string | null>(null);
  if (anim && state) spunBet.current = betName(state);
  const net = state ? state.returned - state.lastStake : 0;
  const result = state?.spun && !anim && ball !== undefined ? `${ball} ${colorOf(ball)} · ${spunBet.current ?? betName(state)} ${net > 0 ? `${state.lucky ? '★ ' : ''}+${money(net)}` : `−${money(state.lastStake)}`}` : undefined;

  return (
    <>
      <Stage className="roulette-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={!!anim && elapsed < firstEnd + SPIN_MS} />
        <Wheel ballAt={ballAt} landed={landed} />
        {/* Always there (empty before the first spin), so the bets below never move. */}
        <div className="history roulette-history" aria-label="Last numbers">
          {(state?.history ?? []).slice(anim ? 1 : 0, (anim ? 1 : 0) + 7).map((n, i) => <span key={i} className={`num ${colorOf(n)}`}>{n}</span>)}
        </div>
        <div className="outside-bets" role="radiogroup" aria-label="Your bet">
          {OUTSIDE.flatMap((o) => [
            <button key={o.bet} role="radio" aria-checked={state?.bet === o.bet} className={`obet ${o.bet}${state?.bet === o.bet ? ' on' : ''}`} onClick={() => choose(o.bet)} disabled={!!anim}>
              <span>{o.label}</span>
              <small>{o.pays}</small>
            </button>,
            // A single number sits with the other bets, ending the top row (user, 2026-09-27).
            ...(o.bet === 'even'
              ? [
                  <button key="straight" role="radio" aria-checked={state?.bet === 'straight'} className={`obet single${state?.bet === 'straight' ? ' on' : ''}`} onClick={() => setNumbers(true)} disabled={!!anim} aria-label={state?.bet === 'straight' ? `Single number: ${state.number}` : 'Pick a single number'}>
                    <span>{state?.bet === 'straight' ? `#${state.number}` : 'Number'}</span>
                    <small>35:1</small>
                  </button>,
                ]
              : []),
          ])}
        </div>
        {numbers && (
          <div className="sheet number-sheet">
            <div className="numbers-head"><span>Single number · pays 35:1</span><button className="waiter-btn" onClick={() => setNumbers(false)}>Close</button></div>
            <div className="numbers">
              {Array.from({ length: 37 }, (_, n) => (
                <button key={n} className={`num-btn ${colorOf(n)}${state?.bet === 'straight' && state.number === n ? ' on' : ''}${n === 0 ? ' zero' : ''}`} onClick={() => choose('straight', n)}>{n}</button>
              ))}
            </div>
          </div>
        )}
        <StatusLine snapshot={snapshot} result={result} tone={net > 0 ? 'win' : state?.spun ? 'loss' : ''} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['pick']} />
    </>
  );
}
